import type { PlatformKind, PlatformResult, StatusBarTheme } from "./types"

const DEFAULT_STATUS_BAR_THEME: StatusBarTheme = {
    backgroundColor: "#000000",
    foreground: "light",
}

interface StatusBarThemeTarget {
    readonly kind: PlatformKind
    setStatusBarTheme(theme: StatusBarTheme): Promise<PlatformResult<void>>
}

export interface StatusBarThemeCoordinator {
    remove(): void
}

function readTheme(element: Element | null): StatusBarTheme {
    if (!element) return DEFAULT_STATUS_BAR_THEME
    const styles = getComputedStyle(element)
    const backgroundColor = styles.getPropertyValue("--wildlark-status-bar-background").trim().toLowerCase()
    const foreground = styles.getPropertyValue("--wildlark-status-bar-foreground").trim()
    if (!/^#[0-9a-f]{6}$/.test(backgroundColor) || (foreground !== "light" && foreground !== "dark")) {
        return DEFAULT_STATUS_BAR_THEME
    }
    return { backgroundColor, foreground }
}

function themeElement(root: HTMLElement): Element | null {
    if (root.classList.contains("code-theme")) return root
    return root.querySelector(".code-theme")
}

function setWebViewUnderlay(theme: StatusBarTheme): void {
    document.documentElement.style.backgroundColor = theme.backgroundColor
    document.body.style.backgroundColor = theme.backgroundColor
    let themeColor = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (!themeColor) {
        themeColor = document.createElement("meta")
        themeColor.name = "theme-color"
        document.head.append(themeColor)
    }
    themeColor.content = theme.backgroundColor
}

export function startStatusBarThemeCoordinator(target: StatusBarThemeTarget, root = document.getElementById("root")): StatusBarThemeCoordinator {
    if ((target.kind !== "ios" && target.kind !== "android") || !root) return { remove: () => undefined }

    let disposed = false
    let pendingFrame: number | null = null
    let lastThemeKey: string | null = null
    let nativeUpdates = Promise.resolve()

    const applyTheme = () => {
        pendingFrame = null
        if (disposed) return
        const theme = readTheme(themeElement(root))
        const themeKey = `${theme.backgroundColor}:${theme.foreground}`
        if (themeKey === lastThemeKey) return
        lastThemeKey = themeKey
        setWebViewUnderlay(theme)
        nativeUpdates = nativeUpdates.then(async () => {
            if (disposed) return
            const result = await target.setStatusBarTheme(theme)
            if (result.status !== "ok") console.warn("[mobile-status-bar] Native status bar update failed")
        })
    }

    const scheduleTheme = () => {
        if (disposed || pendingFrame !== null) return
        pendingFrame = requestAnimationFrame(applyTheme)
    }

    const observer = new MutationObserver(scheduleTheme)
    observer.observe(root, { attributes: true, attributeFilter: ["class"], childList: true, subtree: true })
    scheduleTheme()

    return {
        remove: () => {
            disposed = true
            observer.disconnect()
            if (pendingFrame !== null) cancelAnimationFrame(pendingFrame)
        },
    }
}
